// main.js
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { supabase } from '@/supabaseClient'

// Global styles
import './assets/global.css'
import '@fortawesome/fontawesome-free/css/all.css'
import '/node_modules/flag-icons/css/flag-icons.min.css'
import '@mdi/font/css/materialdesignicons.min.css'
import '@vuepic/vue-datepicker/dist/main.css'

// Vuetify
import { createVuetify } from 'vuetify'
import 'vuetify/styles'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi }
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          primary: '#282E98',
          secondary: '#29B7B4',
          light: '#F9EEE2',
          dark: '#131719',
          accent: '#35b2faff',
          background: '#F9EEE2',
          surface: '#FFFFFF',
          error: '#FF5252',
          success: '#4CAF50',
          warning: '#FFC107',
          white: '#FFFFFF',
        },
      }
    }
  }
})

/* ---------------- Supabase redirect helpers ---------------- */

function parseHashParams() {
  // Supports: #/access_token=...&refresh_token=...&type=signup
  //           #/error=...&error_code=otp_expired&error_description=...
  const raw = window.location.hash.replace(/^#\/?/, '')
  return Object.fromEntries(new URLSearchParams(raw))
}

function friendlyError(code = '', fallback = 'Sign-in link is invalid or expired.') {
  const c = (code || '').toLowerCase()
  if (c.includes('otp_expired')) return 'This email link has expired. Please request a new one.'
  if (c.includes('access_denied')) return 'Access was denied. Please try again.'
  return fallback
}

async function handleSupabaseRedirect() {
  const p = parseHashParams()
  const hasTokens = p.access_token && p.refresh_token
  const hasError = p.error || p.error_code

  if (hasTokens) {
    // Create session so the user is logged in immediately
    await supabase.auth.setSession({
      access_token: p.access_token,
      refresh_token: p.refresh_token,
    })

    // Hydrate your profile in the store
    await store.dispatch('fetchUserData')

    const msg = p.type === 'signup'
      ? 'Email confirmed — you’re signed in!'
      : 'Signed in successfully.'
    sessionStorage.setItem('flash', JSON.stringify({ msg, color: 'success' }))
  } else if (hasError) {
    // Stash a friendly error for App.vue to show via snackbar
    sessionStorage.setItem('flash', JSON.stringify({
      msg: friendlyError(p.error_code || p.error),
      color: 'error'
    }))
  } else {
    return // nothing to handle
  }

  // Triggers a real hashchange that the hash-router listens to
  if (window.location.hash !== '#/') {
    window.location.hash = '/';
  }
}

/* ---------------- Boot order matters ----------------
   1) Handle Supabase hash FIRST (sets/clears session + flash)
   2) Init auth listener (hydrates user if session exists)
   3) Mount the app (App.vue reads flash in mounted and shows snackbar)
----------------------------------------------------- */

await handleSupabaseRedirect()
await store.dispatch('initAuth')

const app = createApp(App)
app.use(router)
app.use(store)
app.use(vuetify)
app.mount('#app')
